import React from 'react';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import CommonCardListShimmer from './CommonCardListShimmer';

const CommonCardList = ({
  data = [],
  onView,
  onEdit,
  onDelete,
  currentPage,
  totalPages,
  onPageChange,
  showSearch,
  setCompaignForSearch,
  compaignSearch,
  loadingFilter,
}) => {
  const formatDate = (value) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString();
  };

  return (
    <div className="p-4 w-full ml-5 rounded-2xl border border-transparent shadow-xl backdrop-blur-md"
      style={{
        background:
          "linear-gradient(160deg, #ffffff 0%, #f5faff 100%) padding-box, linear-gradient(125deg, rgba(130, 168, 216, 0.45) 0%, rgba(122, 163, 214, 0.45) 50%, rgba(140, 180, 226, 0.45) 100%) border-box",
        boxShadow: "0 12px 24px rgba(23, 61, 111, 0.12)",
      }}
    >

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mb-4 rounded-xl border border-slate-200 bg-white px-3 py-2">
          <span className="text-sm text-slate-700">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-lg disabled:opacity-50 text-slate-700 border border-slate-200 bg-white hover:bg-slate-100 transition"
            >
              Prev
            </button>
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                Math.abs(currentPage - page) <= 2
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-3 py-1 rounded-lg border transition ${currentPage === page
                        ? 'text-white border-transparent bg-gradient-to-r from-indigo-500 to-blue-500'
                        : 'text-slate-700 border-slate-200 bg-white hover:bg-slate-100'
                      }`}
                  >
                    {page}
                  </button>
                );
              } else if (
                (page === currentPage - 3 && page !== 1) ||
                (page === currentPage + 3 && page !== totalPages)
              ) {
                return <span key={page}>...</span>;
              }
              return null;
            })}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-lg disabled:opacity-50 text-slate-700 border border-slate-200 bg-white hover:bg-slate-100 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loadingFilter ? (
        <CommonCardListShimmer />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data?.length === 0 ? (
            <div className="col-span-full text-center border border-red-300 bg-red-50/90 p-4 rounded-lg text-red-700 font-medium">
              No results found.
            </div>
          ) : (
            data.map((item) => (
              <div
                key={item.id}
                className="rounded-2xl border border-transparent p-4 flex flex-col justify-between h-full shadow-lg hover:shadow-2xl transition-transform hover:-translate-y-1"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(239,246,255,0.96) 100%) padding-box, linear-gradient(125deg, rgba(14,165,233,0.3) 0%, rgba(59,130,246,0.4) 50%, rgba(99,102,241,0.4) 100%) border-box",
                }}
              >
                <div>
                  <h4 className="text-sm text-gray-500 mb-1 truncate">Campaign ID: {item.id}</h4>
                  <h3 className="text-lg font-bold mb-2 text-slate-800">{item.title}</h3>
                  <p className="text-sm mb-3 min-h-[60px] text-slate-600">{item.description}</p>
                  {item.images?.length > 0 && (
                    <img
                      src={item?.images[0]}
                      alt="campaign"
                      className="w-full h-36 object-cover rounded-xl mb-3 border border-white shadow-sm"
                    />
                  )}
                  <p className="text-slate-700"><strong>Amount:</strong> {item.amount ?? "N/A"}</p>
                  <p className="flex items-center gap-2 text-slate-700">
                    <strong>Status:</strong>{" "}
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full text-xs text-white ${item.status
                          ? 'bg-gradient-to-r from-emerald-500 to-green-500'
                          : 'bg-gradient-to-r from-rose-500 to-red-500'
                        }`}
                    >
                      {item.status ? "Active" : "Inactive"}
                    </span>
                  </p>
                  <p className="text-slate-700"><strong>Modified:</strong> {formatDate(item.modified_at)}</p>
                </div>
                <div className="flex justify-between mt-4">
                  <button onClick={() => onView(item)} className="h-9 w-9 rounded-lg text-blue-700 bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition">
                    <FaEye />
                  </button>
                  <button onClick={() => onEdit(item)} className="h-9 w-9 rounded-lg text-indigo-700 bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center transition">
                    <FaEdit />
                  </button>
                  <button onClick={() => onDelete(item)} className="h-9 w-9 rounded-lg text-red-700 bg-red-100 hover:bg-red-200 flex items-center justify-center transition">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CommonCardList;
